import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { spacesApi, SPACE_TYPES } from '../utils/api/spaces';

/**
 * SpacesContext provides global state management for spaces
 */
const SpacesContext = createContext(null);

/**
 * SpacesProvider component
 * Wraps the application to provide space state and methods
 */
export const SpacesProvider = ({ children }) => {
  // State
  const [spaces, setSpaces] = useState([]);
  const [activeSpaceId, setActiveSpaceId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Load all spaces from the API with automatic retry for transient failures
   */
  const loadSpaces = useCallback(async (includeHidden = false, { maxRetries = 3, retryDelay = 1000 } = {}) => {
    setLoading(true);
    setError(null);

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const loadedSpaces = await spacesApi.list(includeHidden);
        setSpaces(loadedSpaces);

        // Set default active space if none selected
        if (loadedSpaces.length > 0) {
          setActiveSpaceId(prevId => {
            // Keep current selection if still valid
            if (prevId && loadedSpaces.find(s => s.space_id === prevId)) {
              return prevId;
            }
            // Use first visible space by display_order (left-most tab)
            const visibleSpaces = loadedSpaces
              .filter(s => !s.is_hidden)
              .sort((a, b) => a.display_order - b.display_order);
            const firstSpace = visibleSpaces[0] || loadedSpaces[0];
            return firstSpace ? firstSpace.space_id : null;
          });
        }

        setLoading(false);
        return loadedSpaces;
      } catch (err) {
        const isLastAttempt = attempt === maxRetries;
        if (isLastAttempt) {
          console.error('Failed to load spaces after retries:', err);
          setError(err.message || 'Failed to load spaces');
          setLoading(false);
          return null;
        }
        // Wait before retrying with exponential backoff
        console.warn(`Failed to load spaces (attempt ${attempt + 1}/${maxRetries + 1}), retrying...`);
        await new Promise(resolve => setTimeout(resolve, retryDelay * Math.pow(2, attempt)));
      }
    }
  }, []);

  /**
   * Get the currently active space object
   */
  const activeSpace = spaces.find(s => s.space_id === activeSpaceId) || null;

  /**
   * Get visible spaces (not hidden)
   */
  const visibleSpaces = spaces.filter(s => !s.is_hidden);

  /**
   * Get spaces by type
   */
  const getSpacesByType = useCallback((type) => {
    return spaces.filter(s => s.space_type === type);
  }, [spaces]);

  /**
   * Find a space by ID
   */
  const getSpaceById = useCallback((spaceId) => {
    return spaces.find(s => s.space_id === spaceId) || null;
  }, [spaces]);

  /**
   * Find a space by name
   */
  const getSpaceByName = useCallback((name) => {
    return spaces.find(s => s.name.toLowerCase() === name.toLowerCase()) || null;
  }, [spaces]);

  /**
   * Create a new space
   */
  const createSpace = useCallback(async (spaceData) => {
    try {
      const newSpace = await spacesApi.create(spaceData);
      setSpaces(prev => [...prev, newSpace].sort((a, b) => a.display_order - b.display_order));
      return newSpace;
    } catch (err) {
      console.error('Failed to create space:', err);
      throw err;
    }
  }, []);

  /**
   * Update an existing space
   */
  const updateSpace = useCallback(async (spaceId, updates) => {
    try {
      const updatedSpace = await spacesApi.update(spaceId, updates);
      setSpaces(prev => prev.map(s => 
        s.space_id === spaceId ? updatedSpace : s
      ).sort((a, b) => a.display_order - b.display_order));
      return updatedSpace;
    } catch (err) {
      console.error('Failed to update space:', err);
      throw err;
    }
  }, []);

  /**
   * Delete a space
   */
  const deleteSpace = useCallback(async (spaceId) => {
    try {
      await spacesApi.delete(spaceId);
      setSpaces(prev => prev.filter(s => s.space_id !== spaceId));
      
      // If deleted space was active, switch to first available
      if (activeSpaceId === spaceId) {
        const remaining = spaces.filter(s => s.space_id !== spaceId);
        if (remaining.length > 0) {
          setActiveSpaceId(remaining[0].space_id);
        } else {
          setActiveSpaceId(null);
        }
      }
      
      return true;
    } catch (err) {
      console.error('Failed to delete space:', err);
      throw err;
    }
  }, [activeSpaceId, spaces]);

  /**
   * Toggle space visibility (is_hidden)
   */
  const toggleSpaceVisibility = useCallback(async (spaceId) => {
    const space = spaces.find(s => s.space_id === spaceId);
    if (!space) return;
    
    return updateSpace(spaceId, { is_hidden: !space.is_hidden });
  }, [spaces, updateSpace]);

  /**
   * Reorder spaces
   */
  const reorderSpaces = useCallback(async (orderedSpaceIds) => {
    try {
      // Update display_order for each space
      const updates = orderedSpaceIds.map((id, index) => 
        spacesApi.update(id, { display_order: index + 1 })
      );
      
      await Promise.all(updates);
      await loadSpaces();
    } catch (err) {
      console.error('Failed to reorder spaces:', err);
      throw err;
    }
  }, [loadSpaces]);

  // Load spaces on mount
  useEffect(() => {
    loadSpaces(false); // Don't include hidden by default
  }, [loadSpaces]);

  // Context value
  const value = {
    // State
    spaces,
    visibleSpaces,
    activeSpaceId,
    activeSpace,
    loading,
    error,
    
    // Setters
    setActiveSpaceId,
    
    // Methods
    loadSpaces,
    getSpaceById,
    getSpaceByName,
    getSpacesByType,
    createSpace,
    updateSpace,
    deleteSpace,
    toggleSpaceVisibility,
    reorderSpaces,
    
    // Constants
    SPACE_TYPES
  };

  return (
    <SpacesContext.Provider value={value}>
      {children}
    </SpacesContext.Provider>
  );
};

/**
 * useSpaces hook for consuming the SpacesContext
 */
export const useSpaces = () => {
  const context = useContext(SpacesContext);
  if (!context) {
    throw new Error('useSpaces must be used within a SpacesProvider');
  }
  return context;
};

/**
 * Export context for advanced use cases
 */
export { SpacesContext };


